/**
 * Centralized contract address configuration.
 *
 * Source priority (highest → lowest):
 *   1. Runtime addresses fetched from the backend (/api/blockchain/addresses)
 *   2. REACT_APP_* environment variables  — used in Docker / CI builds
 *   3. src/contracts/addresses.json        — updated by export-to-hub.js;
 *                                            CRA hot-reloads this file automatically,
 *                                            so no frontend restart is needed after redeployment.
 *
 * refreshContractAddresses() should be called once at app startup so the
 * frontend picks up freshly-deployed addresses even when the Docker image
 * was built before the contract-deployer ran.
 */
import jsonAddresses from '../contracts/addresses.json';

const addr = (envKey, jsonKey) =>
  process.env[envKey] || jsonAddresses[jsonKey] || '';

export const CONTRACT_ADDRESSES = {
  // Core LiveBetter smart contracts
  LiveBetterToken:      addr('REACT_APP_LBT_TOKEN_ADDRESS',              'LiveBetterToken'),
  StakeholderRegistry:  addr('REACT_APP_STAKEHOLDER_REGISTRY_ADDRESS',   'StakeholderRegistry'),
  DataAssetRegistry:    addr('REACT_APP_DATA_ASSET_REGISTRY_ADDRESS',    'DataAssetRegistry'),
  ServiceRegistry:      addr('REACT_APP_SERVICE_REGISTRY_ADDRESS',       'ServiceRegistry'),
  DataNotarization:     addr('REACT_APP_DATA_NOTARIZATION_ADDRESS',      'DataNotarization'),
  DataServiceAgreement: addr('REACT_APP_DATA_SERVICE_AGREEMENT_ADDRESS', 'DataServiceAgreement'),
  EUBSOContribution:    addr('REACT_APP_EUBSO_CONTRIBUTION_ADDRESS',     'EUBSOContribution'),
  LiveBetterHub:        addr('REACT_APP_LIVEBETTER_HUB_ADDRESS',         'LiveBetterHub'),

  // Legacy contract
  DataServiceExchange:        addr('REACT_APP_DATA_SERVICE_EXCHANGE_ADDRESS', 'DataServiceExchange'),
};

/**
 * Fetches contract addresses from the backend and updates CONTRACT_ADDRESSES
 * in-place.  Returns true if any address was updated, false otherwise.
 * Silently falls back to build-time values on error (backend unreachable, etc.).
 */
export const refreshContractAddresses = async () => {
  try {
    const apiBase = process.env.REACT_APP_API_URL || '/api';
    const res = await fetch(`${apiBase}/blockchain/addresses`);
    if (!res.ok) return false;
    const fresh = await res.json();
    let updated = false;
    for (const key of Object.keys(CONTRACT_ADDRESSES)) {
      if (fresh[key] && fresh[key] !== CONTRACT_ADDRESSES[key]) {
        CONTRACT_ADDRESSES[key] = fresh[key];
        updated = true;
      }
    }
    return updated;
  } catch {
    // Backend not reachable yet — keep build-time addresses
    return false;
  }
};

export const EXPECTED_CHAIN_ID = Number(process.env.REACT_APP_CHAIN_ID || '31337');

export const BLOCK_EXPLORER_BASE_URL = process.env.REACT_APP_BLOCK_EXPLORER_BASE_URL || '';

/**
 * Agreement status enum matching the Solidity DataServiceAgreement.Status enum.
 */
export const AGREEMENT_STATUS = {
  PENDING: 0,
  COUNTERSIGNED: 1,
  ACTIVE: 2,
  FULFILLED: 3,
  DISPUTED: 4,
  CANCELLED: 5,
  EXPIRED: 6,
};

export const AGREEMENT_STATUS_LABELS = {
  [AGREEMENT_STATUS.PENDING]: 'Pending',
  [AGREEMENT_STATUS.COUNTERSIGNED]: 'Countersigned',
  [AGREEMENT_STATUS.ACTIVE]: 'Active',
  [AGREEMENT_STATUS.FULFILLED]: 'Fulfilled',
  [AGREEMENT_STATUS.DISPUTED]: 'Disputed',
  [AGREEMENT_STATUS.CANCELLED]: 'Cancelled',
  [AGREEMENT_STATUS.EXPIRED]: 'Expired',
};

/**
 * Data asset types matching DataAssetRegistry constants.
 */
export const DATA_ASSET_TYPES = {
  STATIC_BUILDING: 0,
  DYNAMIC_METERED: 1,
  EPC_DOCUMENT: 2,
  INSPECTION_REPORT: 3,
  RENOVATION_PLAN: 4,
  FLEXIBILITY_PROFILE: 5,
  EU_BSO_COMPLIANT: 6,
};

export const DATA_ASSET_TYPE_LABELS = {
  [DATA_ASSET_TYPES.STATIC_BUILDING]: 'Static Building',
  [DATA_ASSET_TYPES.DYNAMIC_METERED]: 'Dynamic Metered',
  [DATA_ASSET_TYPES.EPC_DOCUMENT]: 'EPC Document',
  [DATA_ASSET_TYPES.INSPECTION_REPORT]: 'Inspection Report',
  [DATA_ASSET_TYPES.RENOVATION_PLAN]: 'Renovation Plan',
  [DATA_ASSET_TYPES.FLEXIBILITY_PROFILE]: 'Flexibility Profile',
  [DATA_ASSET_TYPES.EU_BSO_COMPLIANT]: 'EU BSO Compliant',
};

/**
 * StakeholderRegistry role bit-flags.
 */
export const STAKEHOLDER_ROLES = {
  BUILDING_OWNER: 1,
  ESCO: 2,
  FINANCIAL_INSTITUTION: 4,
  DATA_AGGREGATOR: 8,
  ENERGY_SERVICE: 16,
  REGULATOR: 32,
};
